/* The printed bill, as one HTML document for utils/printHtml. Built from the
   server's print data (GET …/bill/print/, through mapBillPrint), whose rows already fold the meals
   grid into one row per item and add up exactly to the bill total. Every
   value is escaped: names, addresses and descriptions are typed by people. */
import { escapeHtml } from "../applicationForm/formTemplates";
import { fmtDateTime } from "../adminHelpers";
import { formatRupees } from "../../../utils/money";
import { MODE_OPTIONS } from "./BillPayments";

const e = escapeHtml;
const money = (v) => e(formatRupees(v));
const qty = (v) => e(String(Number(v)));

const STATUS_STAMP = { DRAFT: "DRAFT — NOT A FINAL BILL", CANCELLED: "CANCELLED" };
const KIND = { ADVANCE: "Advance", SETTLEMENT: "Settlement", REFUND: "Refund" };

export function billHtml({ bill, header: h, booking: b, stays: stayList, rows: printRows, taxSummary, amountInWords, payments }) {
  const stamp = STATUS_STAMP[bill.status];
  const anyGst = printRows.some((r) => Number(r.cgst) || Number(r.sgst));
  const rows = printRows.map((r, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td>${e(r.description)}${r.hsnSac ? `<div class="muted">HSN/SAC ${e(r.hsnSac)}</div>` : ""}</td>
      <td>${e(r.categoryName)}</td>
      <td class="r">${qty(r.quantity)}${r.unitLabel ? ` ${e(r.unitLabel)}` : ""}</td>
      <td class="r">${money(r.unitPrice)}</td>
      <td class="r">${money(r.taxable)}</td>
      ${anyGst ? `<td class="r">${money(r.cgst)}</td><td class="r">${money(r.sgst)}</td>` : ""}
      <td class="r"><b>${money(r.total)}</b></td>
    </tr>`).join("");
  const headings = (bill.categoryTotals || []).map((c) =>
    `<tr><td>${e(c.name)}</td><td class="r">${money(c.total)}</td></tr>`).join("");
  const taxRows = anyGst ? taxSummary.filter((t) => Number(t.cgst) || Number(t.sgst)).map((t) => `
    <tr><td>CGST ${e(Number(t.cgstPercent))}% + SGST ${e(Number(t.sgstPercent))}% on ${money(t.taxable)}</td>
    <td class="r">${money(t.cgst)} + ${money(t.sgst)}</td></tr>`).join("") : "";
  const pays = (payments || []).filter((p) => !p.isVoid).map((p) => `
    <tr><td>${e(fmtDateTime(p.receivedAt))}</td><td>${e(KIND[p.kind] || p.kind)}</td>
    <td>${e(MODE_OPTIONS.find((m) => m.value === p.mode)?.text || p.mode)}${p.receiptNumber ? ` · slip ${e(p.receiptNumber)}` : ""}</td>
    <td class="r">${p.kind === "REFUND" ? "− " : ""}${money(p.amount)}</td></tr>`).join("");
  const s = bill.paymentSummary;
  const stays = stayList.map((st) =>
    `<div>${e(st.rooms.join(" → "))}: ${e(fmtDateTime(st.checkedIn))} to ${e(fmtDateTime(st.checkedOut))} (${e(st.days)} day(s))</div>`,
  ).join("");

  return `<!doctype html><html><head><meta charset="utf-8">
<title>${e(`Bill ${bill.billNumber}`)}</title>
<style>
  @page { size: A4; margin: 12mm; }
  body { font-family: Arial, sans-serif; font-size: 11.5px; color: #111; }
  h1 { font-size: 18px; margin: 0; } .muted { color: #555; font-size: 10px; }
  .head { text-align: center; border-bottom: 2px solid #111; padding-bottom: 6px; margin-bottom: 8px; }
  .meta { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th, td { border: 1px solid #999; padding: 3px 5px; vertical-align: top; }
  th { background: #eee; } .r { text-align: right; } .c { text-align: center; }
  .totals { width: 55%; margin-left: auto; } .grand td { font-size: 14px; font-weight: bold; }
  .stamp { border: 2px solid #b00; color: #b00; text-align: center; font-weight: bold; padding: 4px; margin-bottom: 8px; }
  .sign { display: flex; justify-content: space-between; margin-top: 40px; }
</style></head><body>
${stamp ? `<div class="stamp">${e(stamp)}</div>` : ""}
<div class="head">
  <h1>${e(h.name)}</h1>
  <div>${e(h.address)}</div>
  <div class="muted">${[h.phone && `Phone ${h.phone}`, h.email, h.gstin && `GSTIN ${h.gstin}`].filter(Boolean).map(e).join(" · ")}</div>
  <div style="margin-top:4px;font-weight:bold">PROFORMA INVOICE</div>
</div>
<div class="meta">
  <div>
    <div><b>Bill no.</b> ${e(bill.billNumber)}</div>
    <div><b>Date</b> ${e(fmtDateTime(bill.finalizedAt || bill.createdAt))}</div>
    <div><b>Booking</b> ${e(b.bookingNumber)} · ${e(b.bookingType)}${bill.isOfficial ? " (no GST)" : ""}</div>
  </div>
  <div>
    <div><b>Guest</b> ${e(b.guestName)}${b.guestDesignation ? `, ${e(b.guestDesignation)}` : ""}</div>
    <div><b>Billed to</b> ${e(bill.billedToName || b.guestName)}${bill.billedToGstin ? ` · GSTIN ${e(bill.billedToGstin)}` : ""}</div>
    ${bill.billedToAddress ? `<div>${e(bill.billedToAddress)}</div>` : ""}
    <div><b>Applicant</b> ${e(b.applicantName)} (${e(b.employeeId)}), ${e(b.department)}</div>
    <div><b>Payment by</b> ${e(b.paymentResponsibility)}</div>
  </div>
</div>
<div style="margin-bottom:8px"><b>Stay</b>${stays}</div>
<table>
  <thead><tr><th>#</th><th>Description</th><th>Heading</th><th>Qty</th><th>Rate</th><th>Amount</th>
  ${anyGst ? "<th>CGST</th><th>SGST</th>" : ""}<th>Total</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
<table class="totals">
  ${headings}
  <tr><td>Amount before GST</td><td class="r">${money(bill.taxableTotal)}</td></tr>
  ${taxRows}
  <tr><td>Round off</td><td class="r">${money(bill.roundOff)}</td></tr>
  <tr class="grand"><td>Bill total</td><td class="r">${money(bill.grandTotal)}</td></tr>
</table>
<div><b>${e(amountInWords)}</b></div>
${pays ? `<h3 style="font-size:12px;margin:10px 0 4px">Payments</h3>
<table><thead><tr><th>When</th><th>Type</th><th>Mode</th><th>Amount</th></tr></thead><tbody>${pays}</tbody></table>` : ""}
${s ? `<table class="totals"><tr><td>Paid</td><td class="r">${money(s.netPaid)}</td></tr>
  ${Number(s.refundDue) > 0
    ? `<tr class="grand"><td>To refund</td><td class="r">${money(s.refundDue)}</td></tr>`
    : `<tr class="grand"><td>${s.settled ? "Settled" : "Balance due"}</td><td class="r">${money(s.balanceDue)}</td></tr>`}
</table>` : ""}
${bill.notes ? `<div><b>Notes</b> ${e(bill.notes)}</div>` : ""}
<div class="sign"><div>Guest's signature</div><div>For ${e(h.name)}</div></div>
</body></html>`;
}
